/**
 * 플레이어(검사) 도트 아트 생성.
 *
 * 모든 프레임은 48x30 의 동일한 캔버스에 그려지고, 캐릭터는 (14, 4) 위치에
 * 고정 배치된다. 덕분에 애니메이션을 바꿔도 스프라이트가 튀지 않으며,
 * 원점을 (0.5, 1) 로 두면 발바닥이 정확히 바닥에 닿는다.
 */

import { blank, ellipse, outline, px, rect, stamp, type Grid } from './grid';

export const PLAYER_FRAME = { width: 48, height: 30 };
const OX = 14;
const OY = 4;

type ArmPose = 'side' | 'up' | 'forward' | 'high' | 'low';
type LegPose = 'stand' | 'walkA' | 'walkB' | 'jump';

interface CharOpts {
  arms: ArmPose;
  legs: LegPose;
  /** 몸 전체 상하 흔들림 */
  bob?: number;
  /** 뒤통수(사다리/밧줄 등반) */
  back?: boolean;
}

function drawHead(g: Grid, ox: number, oy: number, back: boolean): void {
  const cx = ox + 10;
  // 머리카락 실루엣 (원작처럼 머리가 크고 둥글게)
  ellipse(g, cx, oy + 6, 5.8, 6, 'h');
  ellipse(g, cx - 2, oy + 2.4, 3, 1.4, 'H');

  if (back) {
    ellipse(g, cx, oy + 8, 4.6, 4.2, 'h');
    ellipse(g, cx + 1, oy + 9, 2.6, 2.2, 'H');
    return;
  }

  ellipse(g, cx, oy + 8.6, 4.8, 4.4, 's');
  rect(g, ox + 5, oy + 1, 11, 3, 'h');
  ellipse(g, cx - 3, oy + 11, 2.2, 1.2, 'S');

  // 눈 (오른쪽을 보는 기준). 흰자 위에 검은 눈동자를 올려 원작처럼 또렷하게.
  rect(g, ox + 8, oy + 7, 3, 4, 'q');
  rect(g, ox + 12, oy + 7, 3, 4, 'q');
  rect(g, ox + 9, oy + 8, 2, 3, 'e');
  rect(g, ox + 13, oy + 8, 2, 3, 'e');
  // 입
  px(g, ox + 11, oy + 12, 'S');
  px(g, ox + 12, oy + 12, 'S');
}

function drawArms(g: Grid, ox: number, oy: number, pose: ArmPose): void {
  const backArm = () => {
    rect(g, ox + 4, oy + 15, 2, 4, 'T');
    rect(g, ox + 4, oy + 19, 2, 1, 'S');
  };

  switch (pose) {
    case 'side':
      backArm();
      rect(g, ox + 14, oy + 15, 2, 4, 't');
      rect(g, ox + 14, oy + 19, 2, 1, 's');
      break;
    case 'up':
      rect(g, ox + 4, oy + 9, 2, 6, 'T');
      rect(g, ox + 4, oy + 9, 2, 1, 'S');
      rect(g, ox + 14, oy + 9, 2, 6, 't');
      rect(g, ox + 14, oy + 9, 2, 1, 's');
      break;
    case 'forward':
      backArm();
      rect(g, ox + 13, oy + 16, 4, 2, 't');
      rect(g, ox + 16, oy + 16, 2, 2, 's');
      break;
    case 'high':
      backArm();
      rect(g, ox + 13, oy + 11, 2, 5, 't');
      rect(g, ox + 13, oy + 11, 2, 2, 's');
      break;
    case 'low':
      backArm();
      rect(g, ox + 13, oy + 17, 4, 2, 't');
      rect(g, ox + 16, oy + 17, 2, 2, 's');
      break;
  }
}

function drawLegs(g: Grid, ox: number, oy: number, pose: LegPose): void {
  const leg = (x: number, y: number, h: number, bootX: number) => {
    rect(g, x, y, 3, h, 'p');
    rect(g, x, y + h - 1, 3, 1, 'P');
    rect(g, bootX, y + h, 4, 2, 'k');
    rect(g, bootX, y + h, 4, 1, 'K');
  };

  switch (pose) {
    case 'stand':
      leg(ox + 6, oy + 20, 4, ox + 5);
      leg(ox + 11, oy + 20, 4, ox + 11);
      break;
    case 'walkA':
      leg(ox + 4, oy + 20, 4, ox + 3);
      leg(ox + 12, oy + 20, 4, ox + 12);
      break;
    case 'walkB':
      leg(ox + 6, oy + 21, 3, ox + 5);
      leg(ox + 11, oy + 20, 4, ox + 11);
      break;
    case 'jump':
      leg(ox + 4, oy + 20, 3, ox + 3);
      leg(ox + 12, oy + 19, 4, ox + 12);
      break;
  }
}

function drawChar(g: Grid, o: CharOpts): void {
  const oy = OY + (o.bob ?? 0);
  drawLegs(g, OX, oy, o.legs);
  rect(g, OX + 8, oy + 13, 4, 1, 'S');
  rect(g, OX + 6, oy + 14, 8, 6, 't');
  rect(g, OX + 6, oy + 14, 8, 1, 'T');
  rect(g, OX + 6, oy + 18, 8, 1, 'T');
  rect(g, OX + 6, oy + 19, 8, 1, 'B');
  drawArms(g, OX, oy, o.arms);
  drawHead(g, OX, oy, o.back ?? false);
}

function swordHorizontal(): Grid {
  const g = blank(17, 5);
  rect(g, 0, 1, 4, 3, 'g');
  rect(g, 4, 0, 2, 5, 'G');
  rect(g, 6, 1, 10, 2, 'w');
  rect(g, 6, 3, 9, 1, 'W');
  px(g, 16, 2, 'w');
  return g;
}

function swordVertical(): Grid {
  const g = blank(5, 15);
  rect(g, 1, 10, 3, 5, 'g');
  rect(g, 0, 9, 5, 1, 'G');
  rect(g, 1, 1, 3, 8, 'w');
  rect(g, 3, 1, 1, 8, 'W');
  px(g, 2, 0, 'w');
  return g;
}

/** 대기/걷기 자세에서 옆에 내려 든 검 */
function swordRest(): Grid {
  const g = blank(5, 12);
  rect(g, 1, 0, 3, 3, 'g');
  rect(g, 0, 3, 5, 1, 'G');
  rect(g, 1, 4, 3, 7, 'w');
  rect(g, 3, 4, 1, 7, 'W');
  px(g, 2, 11, 'w');
  return g;
}

function swordDiagonal(down: boolean): Grid {
  const g = blank(16, 12);
  rect(g, 0, down ? 0 : 8, 4, 3, 'g');
  for (let i = 0; i < 12; i++) {
    const y = down ? 1 + Math.floor(i * 0.72) : 9 - Math.floor(i * 0.72);
    px(g, 4 + i, y, 'w');
    px(g, 4 + i, y + 1, 'w');
    px(g, 4 + i, y + 2, 'W');
  }
  return g;
}

function frame(opts: CharOpts, sword?: { grid: Grid; x: number; y: number }): Grid {
  const g = blank(PLAYER_FRAME.width, PLAYER_FRAME.height);
  if (sword) stamp(g, sword.grid, sword.x, sword.y);
  drawChar(g, opts);
  return outline(g);
}

/** 손에 검을 들고 있는 기본 자세용 배치 */
const REST_SWORD = { x: OX + 15, y: OY + 14 };

export const PLAYER_ANIMS = {
  stand: (): Grid[] => [
    frame({ arms: 'side', legs: 'stand' }, { grid: swordRest(), ...REST_SWORD }),
    frame({ arms: 'side', legs: 'stand', bob: 1 }, {
      grid: swordRest(),
      x: REST_SWORD.x,
      y: REST_SWORD.y + 1,
    }),
  ],

  walk: (): Grid[] => [
    frame({ arms: 'side', legs: 'walkB' }, { grid: swordRest(), ...REST_SWORD }),
    frame({ arms: 'side', legs: 'walkA', bob: -1 }, {
      grid: swordRest(),
      x: REST_SWORD.x,
      y: REST_SWORD.y - 1,
    }),
    frame({ arms: 'side', legs: 'walkB' }, { grid: swordRest(), ...REST_SWORD }),
    frame({ arms: 'side', legs: 'walkA', bob: -1 }, {
      grid: swordRest(),
      x: REST_SWORD.x + 1,
      y: REST_SWORD.y - 1,
    }),
  ],

  jump: (): Grid[] => [
    frame({ arms: 'up', legs: 'jump' }, { grid: swordRest(), x: OX + 16, y: OY + 8 }),
  ],

  hurt: (): Grid[] => [
    frame({ arms: 'up', legs: 'jump', bob: 1 }, { grid: swordRest(), x: OX + 16, y: OY + 9 }),
  ],

  attack: (): Grid[] => [
    frame({ arms: 'high', legs: 'stand' }, { grid: swordVertical(), x: OX + 12, y: OY + 1 }),
    frame({ arms: 'forward', legs: 'walkA' }, { grid: swordHorizontal(), x: OX + 16, y: OY + 14 }),
    frame({ arms: 'low', legs: 'stand' }, { grid: swordDiagonal(true), x: OX + 16, y: OY + 15 }),
  ],

  swing: (): Grid[] => [
    frame({ arms: 'low', legs: 'stand' }, { grid: swordDiagonal(true), x: OX + 16, y: OY + 15 }),
    frame({ arms: 'forward', legs: 'walkA' }, { grid: swordHorizontal(), x: OX + 16, y: OY + 14 }),
    frame({ arms: 'high', legs: 'walkB' }, { grid: swordDiagonal(false), x: OX + 14, y: OY + 4 }),
  ],

  ladder: (): Grid[] => [
    frame({ arms: 'up', legs: 'walkA', back: true }),
    frame({ arms: 'up', legs: 'walkB', back: true, bob: -1 }),
  ],

  rope: (): Grid[] => [
    frame({ arms: 'up', legs: 'stand', back: true }),
    frame({ arms: 'up', legs: 'walkB', back: true, bob: -1 }),
  ],
};
